/**
 * MetaProbe Cut Detector
 *
 * Whole-clip shot-boundary detection to verify a clip is a single continuous
 * take (or to list its cuts). Decodes every frame in presentation order via
 * WebCodecs (fed by an mp4box demux), reduces each frame to a coarse colour
 * histogram + mean luma, then flags:
 *   - hard cuts  : an isolated appearance spike above an ADAPTIVE local threshold
 *   - fades      : a mid-clip ramp to/from black
 * (Dissolves and the experimental jump-cut heuristic are planned follow-ups.)
 *
 * Pure client-side. WebCodecs decodes locally; nothing is uploaded.
 *
 * LIMITATIONS
 *   - Container: MP4 / MOV (ISOBMFF) only — that's what mp4box demuxes. WebM/MKV
 *     return { scanned:false, reason:'unsupported-container' }.
 *   - Codec: whatever VideoDecoder can configure (H.264/VP9/AV1 broadly; HEVC
 *     spotty). Unsupported => { scanned:false, reason:'unsupported-codec' }.
 *   - Heavy: decodes the whole clip on the user's CPU/GPU, so it's button-driven
 *     with progress, not auto-run.
 */

/* global VideoDecoder, EncodedVideoChunk */
import { createFile as createMP4File, DataStream } from 'mp4box';

// --- Tunables -----------------------------------------------------------------
const DOWNSCALE_MAX_SIDE = 64;   // longest side of the analysis canvas
const HIST_BINS_PER_CH = 4;      // 4x4x4 = 64-bin joint RGB histogram
const MEDIAN_WINDOW = 15;        // +/- frames for the adaptive local baseline
const PEAK_WINDOW = 6;           // +/- frames a hard cut must be the local max over
const HARD_CUT_K = 3.0;          // spike must exceed K x local median...
const HARD_CUT_FLOOR = 0.35;     // ...and this absolute floor (histogram L1/2, 0..1)
const FADE_BLACK_LUMA = 12;      // mean luma below this counts as "black" for fades
const DECODE_QUEUE_MAX = 24;     // backpressure: pause feeding above this depth
const MAX_FRAMES = 20000;        // safety cap for pathological clips

// --- Experimental jump-cut heuristic -----------------------------------------
// A jump cut is a discontinuity *within* a scene: the appearance barely changes
// but the subject "teleports". We catch it as a spike in motion-compensated
// residual (block-matched to the previous frame) that survives even after the
// best local shift. Inherently noisy — flagged low-confidence.
const JUMP_BLOCKS_X = 4;         // block grid for motion estimation
const JUMP_BLOCKS_Y = 3;
const JUMP_SEARCH = 4;           // +/- px search range on the downscaled plane
const JUMP_FLOOR = 0.06;         // min per-pixel residual (0..1) to consider
const JUMP_K = 2.5;              // residual must exceed K x local median
const JUMP_MIN_APPEARANCE = 0.06; // require *some* visible change (rejects static)
const JUMP_MAX_CONFIDENCE = 0.6; // never present a jump cut as high-confidence

/** Is whole-clip cut scanning possible in this browser at all? */
export function isCutScanSupported() {
    return typeof VideoDecoder !== 'undefined'
        && typeof EncodedVideoChunk !== 'undefined';
}

/**
 * Analyze a video file for cuts.
 *
 * @param {File} file
 * @param {{ onProgress?: (fraction:number)=>void, signal?: AbortSignal }} [opts]
 * @returns {Promise<{
 *   scanned: boolean,
 *   reason?: string,
 *   framesAnalyzed: number,
 *   truncated: boolean,
 *   fps: number,
 *   durationSeconds: number,
 *   continuous: boolean,
 *   cuts: Array<{ time:number, frame:number, type:'hard'|'fade', confidence:number }>,
 * }>}
 */
export async function analyzeCuts(file, opts = {}) {
    const { onProgress, signal } = opts;
    const result = {
        scanned: false,
        framesAnalyzed: 0,
        truncated: false,
        fps: 0,
        durationSeconds: 0,
        continuous: true,
        cuts: [],
    };

    if (!file || !file.type?.startsWith('video/')) {
        result.reason = 'not-video';
        return result;
    }
    if (!isCutScanSupported()) {
        result.reason = 'webcodecs-unsupported';
        return result;
    }

    let demux;
    try {
        demux = await demuxVideo(file);
    } catch (e) {
        result.reason = e?.message === 'no-video-track' ? 'unsupported-container' : 'demux-failed';
        return result;
    }
    const { config, samples, track } = demux;

    // Confirm the browser can actually decode this codec before we commit.
    try {
        const support = await VideoDecoder.isConfigSupported(config);
        if (!support?.supported) {
            result.reason = 'unsupported-codec';
            return result;
        }
    } catch (e) {
        result.reason = 'unsupported-codec';
        return result;
    }

    result.fps = track.timescale && track.nb_samples && track.duration
        ? track.nb_samples / (track.duration / track.timescale)
        : 0;
    result.durationSeconds = track.duration && track.timescale ? track.duration / track.timescale : 0;

    // Per-frame series (kept small: we don't retain pixels or histograms).
    const times = [];   // presentation time (s) of each frame
    const lumas = [];   // mean luma per frame
    const diffs = [];   // histogram distance to previous frame (diffs[0] = 0)
    const residuals = []; // motion-compensated residual to previous frame (jump cuts)

    const canvas = document.createElement('canvas');
    let ctx = null;
    let prevHist = null;
    let prevPlane = null;
    let processed = 0;
    let truncated = false;
    const totalSamples = Math.min(samples.length, MAX_FRAMES);

    const decoder = new VideoDecoder({
        output: (frame) => {
            try {
                if (processed >= MAX_FRAMES) { truncated = true; return; }
                if (!ctx) {
                    const scale = DOWNSCALE_MAX_SIDE / Math.max(frame.displayWidth, frame.displayHeight);
                    canvas.width = Math.max(1, Math.round(frame.displayWidth * scale));
                    canvas.height = Math.max(1, Math.round(frame.displayHeight * scale));
                    ctx = canvas.getContext('2d', { willReadFrequently: true });
                }
                ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
                const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const { hist, luma, plane } = frameFeatures(data);

                times.push(frame.timestamp / 1e6);
                lumas.push(luma);
                diffs.push(prevHist ? histDistance(prevHist, hist) : 0);
                residuals.push(prevPlane ? motionResidual(prevPlane, plane, canvas.width, canvas.height) : 0);
                prevHist = hist;
                prevPlane = plane;
                processed++;
                if (onProgress && totalSamples) onProgress(Math.min(1, processed / totalSamples));
            } catch (e) {
                // transient frame error — skip this frame
            } finally {
                frame.close();
            }
        },
        error: (e) => { console.warn('VideoDecoder error:', e); },
    });

    try {
        decoder.configure(config);

        for (let i = 0; i < samples.length; i++) {
            if (signal?.aborted) { result.reason = 'aborted'; break; }
            if (processed >= MAX_FRAMES) { truncated = true; break; }
            const s = samples[i];
            // Backpressure so the decode/decoded-frame queues don't balloon.
            while (decoder.decodeQueueSize > DECODE_QUEUE_MAX && !signal?.aborted) {
                await new Promise((r) => setTimeout(r, 0));
            }
            decoder.decode(new EncodedVideoChunk({
                type: s.is_sync ? 'key' : 'delta',
                timestamp: (s.cts / s.timescale) * 1e6,
                duration: (s.duration / s.timescale) * 1e6,
                data: s.data,
            }));
        }
        await decoder.flush();
    } catch (e) {
        console.warn('Cut decode failed:', e);
        if (!times.length) {
            result.reason = 'decode-failed';
            return result;
        }
        // Partial decode — still analyze what we got.
    } finally {
        try { decoder.close(); } catch (e) { /* already closed */ }
    }

    if (signal?.aborted) { result.reason = 'aborted'; return result; }

    result.scanned = true;
    result.framesAnalyzed = times.length;
    result.truncated = truncated;
    if (!result.fps && times.length > 1) {
        // Fall back to deriving fps from observed timestamps.
        const span = times[times.length - 1] - times[0];
        if (span > 0) result.fps = (times.length - 1) / span;
    }

    result.cuts = detectCuts(times, lumas, diffs, residuals);
    result.continuous = !result.cuts.some((c) => c.type === 'hard');
    return result;
}

// ---------------------------------------------------------------------------
// Demux: mp4box -> { decoder config, ordered encoded samples }
// ---------------------------------------------------------------------------
function demuxVideo(file) {
    return new Promise((resolve, reject) => {
        const mp4 = createMP4File();
        const samples = [];
        let resolved = false;

        mp4.onError = (e) => reject(new Error('demux-error: ' + e));

        mp4.onReady = (info) => {
            const track = info.videoTracks && info.videoTracks[0];
            if (!track) { reject(new Error('no-video-track')); return; }

            let description;
            try {
                description = buildDescription(mp4, track.id);
            } catch (e) {
                reject(new Error('no-codec-description'));
                return;
            }

            const config = {
                codec: track.codec,
                codedWidth: track.video ? track.video.width : track.track_width,
                codedHeight: track.video ? track.video.height : track.track_height,
                description,
                hardwareAcceleration: 'no-preference',
                optimizeForLatency: false,
            };

            mp4.setExtractionOptions(track.id, null, { nbSamples: Infinity });
            mp4.onSamples = (_id, _user, newSamples) => {
                for (const s of newSamples) samples.push(s);
            };
            mp4.start();
            mp4.flush();

            if (!resolved) {
                resolved = true;
                resolve({ config, samples, track });
            }
        };

        file.arrayBuffer().then((buf) => {
            buf.fileStart = 0;
            mp4.appendBuffer(buf);
            mp4.flush();
        }).catch(reject);
    });
}

/** Extract the avcC/hvcC/vpcC/av1C decoder-config record as a Uint8Array. */
function buildDescription(mp4, trackId) {
    const trak = mp4.getTrackById(trackId);
    const entries = trak?.mdia?.minf?.stbl?.stsd?.entries || [];
    for (const entry of entries) {
        const box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C;
        if (box) {
            const stream = new DataStream(); // defaults to big-endian (network order)
            box.write(stream);
            // Strip the 8-byte box header (size + type) that write() prepends.
            return new Uint8Array(stream.buffer, 8);
        }
    }
    throw new Error('no-codec-description');
}

// ---------------------------------------------------------------------------
// Per-frame features
// ---------------------------------------------------------------------------
/** 4x4x4 joint RGB histogram (normalized) + Rec.709 mean luma + luma plane. */
function frameFeatures(rgba) {
    const bins = HIST_BINS_PER_CH * HIST_BINS_PER_CH * HIST_BINS_PER_CH;
    const hist = new Float32Array(bins);
    const plane = new Float32Array(rgba.length / 4); // row-major per-pixel luma
    const shift = 8 - Math.log2(HIST_BINS_PER_CH); // 8 - 2 = 6 for 4 bins/ch
    let lumaSum = 0;
    let n = 0;
    for (let i = 0; i < rgba.length; i += 4) {
        const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
        const br = r >> shift, bg = g >> shift, bb = b >> shift;
        hist[(br * HIST_BINS_PER_CH + bg) * HIST_BINS_PER_CH + bb]++;
        const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        plane[n] = y;
        lumaSum += y;
        n++;
    }
    if (n > 0) {
        for (let k = 0; k < bins; k++) hist[k] /= n;
    }
    return { hist, luma: n ? lumaSum / n : 0, plane };
}

/** L1 distance between two normalized histograms, scaled to 0..1. */
function histDistance(a, b) {
    let sum = 0;
    for (let k = 0; k < a.length; k++) sum += Math.abs(a[k] - b[k]);
    return sum / 2; // each histogram sums to 1, so L1 in [0,2]
}

/**
 * Block-matched motion-compensated residual between two luma planes (0..1).
 * Each block finds its best +/-SEARCH shift to the previous frame; we sum the
 * minimum SAD. Smooth motion (pan/zoom) compensates to a low residual; a jump
 * cut leaves a high residual no shift can fix.
 */
function motionResidual(prev, cur, w, h) {
    const S = JUMP_SEARCH;
    const bw = Math.floor(w / JUMP_BLOCKS_X);
    const bh = Math.floor(h / JUMP_BLOCKS_Y);
    if (bw < 2 || bh < 2) {
        // Frame too small to block-match — fall back to plain SAD.
        let s = 0;
        for (let p = 0; p < cur.length; p++) s += Math.abs(cur[p] - prev[p]);
        return cur.length ? s / (cur.length * 255) : 0;
    }
    let total = 0;
    let count = 0;
    for (let by = 0; by < JUMP_BLOCKS_Y; by++) {
        for (let bx = 0; bx < JUMP_BLOCKS_X; bx++) {
            const x0 = bx * bw, y0 = by * bh;
            let best = Infinity;
            for (let dy = -S; dy <= S; dy++) {
                for (let dx = -S; dx <= S; dx++) {
                    let sad = 0;
                    for (let y = 0; y < bh; y++) {
                        const cy = y0 + y;
                        let py = cy + dy;
                        if (py < 0) py = 0; else if (py >= h) py = h - 1;
                        const curRow = cy * w, prevRow = py * w;
                        for (let x = 0; x < bw; x++) {
                            const cx = x0 + x;
                            let px = cx + dx;
                            if (px < 0) px = 0; else if (px >= w) px = w - 1;
                            sad += Math.abs(cur[curRow + cx] - prev[prevRow + px]);
                        }
                    }
                    if (sad < best) best = sad;
                }
            }
            total += best;
            count += bw * bh;
        }
    }
    return count ? total / (count * 255) : 0;
}

// ---------------------------------------------------------------------------
// Cut detection over the per-frame series
// ---------------------------------------------------------------------------
function detectCuts(times, lumas, diffs, residuals) {
    const cuts = [];
    const n = diffs.length;
    if (n < 3) return cuts;

    const flaggedHard = new Set();

    for (let i = 1; i < n; i++) {
        const d = diffs[i];
        if (d < HARD_CUT_FLOOR) continue;

        // Adaptive threshold: K x median of the local neighbourhood (excluding i).
        const lo = Math.max(1, i - MEDIAN_WINDOW);
        const hi = Math.min(n - 1, i + MEDIAN_WINDOW);
        const neighbourhood = [];
        for (let j = lo; j <= hi; j++) if (j !== i) neighbourhood.push(diffs[j]);
        const localMedian = median(neighbourhood);
        const threshold = Math.max(HARD_CUT_FLOOR, HARD_CUT_K * localMedian);
        if (d <= threshold) continue;

        // Must be the dominant local maximum (rejects shoulders of one spike).
        if (!isLocalMax(diffs, i, PEAK_WINDOW)) continue;

        // Suppress a second hit within the peak window.
        let dup = false;
        for (let j = i - PEAK_WINDOW; j < i; j++) if (flaggedHard.has(j)) { dup = true; break; }
        if (dup) continue;
        flaggedHard.add(i);

        const confidence = clamp01((d / threshold - 1) / 2 + 0.5);
        cuts.push({ time: round3(times[i]), frame: i, type: 'hard', confidence: round2(confidence) });
    }

    // Fades: mid-clip dark runs flanked by brighter footage (head/tail handled by
    // the black-frame detector, so we ignore runs touching either end).
    let runStart = -1;
    for (let i = 0; i < lumas.length; i++) {
        const dark = lumas[i] < FADE_BLACK_LUMA;
        if (dark && runStart < 0) runStart = i;
        if ((!dark || i === lumas.length - 1) && runStart >= 0) {
            const rs = runStart;
            const re = dark ? i : i - 1;
            const touchesEnd = rs === 0 || re === lumas.length - 1;
            if (!touchesEnd) {
                const mid = (rs + re) >> 1;
                // Don't double-report if a hard cut already sits in this run.
                const overlapsHard = cuts.some((c) => c.type === 'hard' && c.frame >= rs - 1 && c.frame <= re + 1);
                if (!overlapsHard) {
                    cuts.push({ time: round3(times[mid]), frame: mid, type: 'fade', confidence: 0.6 });
                }
            }
            runStart = -1;
        }
    }

    // Experimental jump cuts: an isolated motion-residual spike that is NOT a
    // full scene change (so not a hard cut) and not in a dark/fade region.
    if (residuals && residuals.length === n) {
        const flaggedJump = new Set();
        for (let i = 1; i < n; i++) {
            const r = residuals[i];
            if (r < JUMP_FLOOR) continue;
            if (diffs[i] >= HARD_CUT_FLOOR) continue;        // scene change => hard cut
            if (diffs[i] < JUMP_MIN_APPEARANCE) continue;    // essentially static
            if (lumas[i] < FADE_BLACK_LUMA * 2) continue;    // dark/fade region

            const lo = Math.max(1, i - MEDIAN_WINDOW);
            const hi = Math.min(n - 1, i + MEDIAN_WINDOW);
            const neighbourhood = [];
            for (let j = lo; j <= hi; j++) if (j !== i) neighbourhood.push(residuals[j]);
            const threshold = Math.max(JUMP_FLOOR, JUMP_K * median(neighbourhood));
            if (r <= threshold) continue;
            if (!isLocalMax(residuals, i, PEAK_WINDOW)) continue;

            // Skip if coincident with a hard cut or a previous jump flag.
            let near = false;
            for (let j = i - PEAK_WINDOW; j <= i + PEAK_WINDOW; j++) {
                if (flaggedHard.has(j) || flaggedJump.has(j)) { near = true; break; }
            }
            if (near) continue;
            flaggedJump.add(i);

            const confidence = Math.min(JUMP_MAX_CONFIDENCE, 0.25 + (r / threshold - 1) * 0.2);
            cuts.push({ time: round3(times[i]), frame: i, type: 'jump', confidence: round2(confidence) });
        }
    }

    cuts.sort((a, b) => a.frame - b.frame);
    return cuts;
}

function isLocalMax(arr, i, w) {
    const lo = Math.max(0, i - w);
    const hi = Math.min(arr.length - 1, i + w);
    for (let j = lo; j <= hi; j++) if (j !== i && arr[j] >= arr[i]) return false;
    return true;
}

function median(arr) {
    if (!arr.length) return 0;
    const s = [...arr].sort((a, b) => a - b);
    const m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

function clamp01(x) { return Math.min(1, Math.max(0, x)); }
function round2(x) { return Math.round(x * 100) / 100; }
function round3(x) { return Math.round(x * 1000) / 1000; }

export default analyzeCuts;
